import React, { useState, useEffect, useRef } from 'react';
import { ChevronUp, ChevronDown, Clock } from 'lucide-react';
import './App.css';

function App() {
  const [started, setStarted] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isAnimating, setIsAnimating] = useState(false);
  const [direction, setDirection] = useState('forward');
  const [emailError, setEmailError] = useState(false);
  const [shake, setShake] = useState(false);
  const inputRef = useRef(null);
  const webhookUrl = 'https://services.leadconnectorhq.com/hooks/I7qmtU21Jfc94jMhle4S/webhook-trigger/50acf9c7-205c-484a-a6bf-ed54854d58fc';

  const questions = [
    {
      id: 'email',
      question: 'What is your email?',
      type: 'text',
      placeholder: 'Type your answer here...',
      required: true
    },
    {
      id: 'agentType',
      question: 'Which AI Agent do you want to set up first?',
      type: 'multiplechoice',
      options: [
        'AI Receptionist',
        'AI Dispatch Service',
        'AI Appointment Setter',
        'AI Telemarketing',
        'AI Ads/Website Agent'
      ],
      required: true
    },
    {
      id: 'features',
      question: 'What specific features would you like to build into your AI voice agent?',
      type: 'textarea',
      placeholder: 'Describe the features you need...',
      required: true
    },
    {
      id: 'goals',
      question: 'What is your number one goal with this AI agent?',
      type: 'multiplechoice',
      options: [
        'Respond to leads faster (within 30 seconds)',
        'Never miss after-hours opportunities',
        'Increase appointment booking rates',
        'Reduce team workload',
        'Scale during peak seasons'
      ],
      required: true
    },
    {
      id: 'telemarketing',
      question: 'Are you currently utilize telemarketing or have interest in building out a telemarketing team in the future?',
      type: 'multiplechoice',
      options: [
        'Yes',
        'No'
      ],
      required: true
    },
    {
      id: 'onlineLeads',
      question: 'Roughly how many online (website, landing pages etc.) leads did you get last month?',
      subtitle: 'Please try to be as accurate as possible so we can set proper baselines to help you improve.',
      type: 'text',
      placeholder: 'Type your answer here...',
      required: true
    },
    {
      id: 'appointments',
      question: 'Of the online leads, roughly how many appointments did you book last month?',
      subtitle: 'Please try to be as accurate as possible so we can set proper baselines to help you improve.',
      type: 'text',
      placeholder: 'Type your answer here...',
      required: true
    },
    {
      id: 'showups',
      question: 'Of the bookings, roughly how many showed up for their appointments?',
      subtitle: 'Please try to be as accurate as possible so we can set proper baselines to help you improve.',
      type: 'text',
      placeholder: 'Type your answer here...',
      required: true
    },
    {
      id: 'deals',
      question: 'Roughly how many deals close from your online leads per month?',
      subtitle: 'Please try to be as accurate as possible so we can set proper baselines to help you improve.',
      type: 'text',
      placeholder: 'Type your answer here...',
      required: true
    },
    {
      id: 'referrals',
      question: 'What are the names of 3 people who are just like you who can take advantage of AI Voice Technology?',
      type: 'textarea',
      placeholder: 'Enter names...',
      required: true
    }
  ];

  useEffect(() => {
    if (inputRef.current && started) {
      inputRef.current.focus();
    }
  }, [currentQuestion, started]);

  useEffect(() => {
    if (!started) return;
    
    const handleGlobalKeyPress = (e) => {
      handleKeyPress(e);
    };
    
    window.addEventListener('keypress', handleGlobalKeyPress);
    return () => window.removeEventListener('keypress', handleGlobalKeyPress);
  }, [currentQuestion, answers, started]);

  const validateEmail = (email) => {
    return email.includes('@') && email.includes('.');
  };

  const triggerShake = () => {
    setShake(true);
    setTimeout(() => setShake(false), 500);
  };

  const handleEmailChange = (value) => {
    setAnswers({ ...answers, email: value });
    // Clear error if email becomes valid
    if (emailError && validateEmail(value)) {
      setEmailError(false);
    }
  };

  const handleMultipleChoiceSelect = (option) => {
    const currentQ = questions[currentQuestion];
    setAnswers({ ...answers, [currentQ.id]: option });
    
    setTimeout(() => {
      if (currentQuestion < questions.length - 1) {
        setDirection('forward');
        setIsAnimating(true);
        setTimeout(() => {
          setCurrentQuestion(currentQuestion + 1);
          setIsAnimating(false);
        }, 300);
      } else {
        handleSubmit();
      }
    }, 400);
  };

  const handleNext = () => {
    const currentQ = questions[currentQuestion];
    const answer = answers[currentQ.id];

    // Email validation
    if (currentQ.id === 'email') {
      if (!answer || !validateEmail(answer)) {
        setEmailError(true);
        triggerShake();
        return;
      }
    }

    if (currentQ.required && (!answer || (Array.isArray(answer) && answer.length === 0))) {
      alert('Please answer this question before continuing');
      return;
    }

    if (currentQuestion < questions.length - 1) {
      setDirection('forward');
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentQuestion(currentQuestion + 1);
        setIsAnimating(false);
      }, 300);
    } else {
      handleSubmit();
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setDirection('backward');
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentQuestion(currentQuestion - 1);
        setIsAnimating(false);
      }, 300);
    }
  };

  const handleSubmit = async () => {
    const formData = {
      email: answers.email,
      agentType: answers.agentType,
      features: answers.features,
      goals: answers.goals,
      telemarketing: answers.telemarketing,
      onlineLeads: answers.onlineLeads,
      appointments: answers.appointments,
      showups: answers.showups,
      deals: answers.deals,
      referrals: answers.referrals,
      submittedAt: new Date().toISOString()
    };

    try {
      await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });
    } catch (error) {
      console.error('Error submitting to webhook:', error);
    }
    
    window.location.href = 'https://onboarding.kingcaller.ai';
  };

  const handleKeyPress = (e) => {
    const currentQ = questions[currentQuestion];
    
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleNext();
      return;
    }
    
    if (currentQ.type === 'multiplechoice') {
      const key = e.key.toLowerCase();
      const index = key.charCodeAt(0) - 97;
      
      if (index >= 0 && index < currentQ.options.length) {
        e.preventDefault();
        const option = currentQ.options[index];
        handleMultipleChoiceSelect(option);
      }
    }
  };

  const progressPercentage = ((currentQuestion + 1) / questions.length) * 100;
  const currentQ = questions[currentQuestion];

  if (!started) {
    return (
      <div className="welcome-screen">
        <div className="welcome-content">
          <h1>To schedule your onboarding, please complete this short survey.</h1>
          <p>The booking page is at the end.</p>
          
          <button onClick={() => setStarted(true)} className="start-button">
            Start
            <div className="timer-badge">
              <Clock className="clock-icon" />
              <span>takes 45 sec</span>
            </div>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="typeform-container">
      <div className="progress-bar">
        <div className="progress-fill" style={{ width: `${progressPercentage}%` }} />
      </div>

      {currentQuestion > 0 && (
        <button onClick={handlePrevious} className="nav-arrow nav-prev">
          <ChevronUp />
        </button>
      )}

      {currentQuestion < questions.length - 1 && (
        <button onClick={handleNext} className="nav-arrow nav-next">
          <ChevronDown />
        </button>
      )}

      <div className="main-content">
        <div className={`question-wrapper ${isAnimating ? (direction === 'forward' ? 'slide-out-up' : 'slide-out-down') : 'slide-in'}`}>
          <div className="question-number">{currentQuestion + 1} →</div>
          <h2 className="question-text">{currentQ.question}</h2>
          {currentQ.subtitle && <p className="question-subtitle">{currentQ.subtitle}</p>}

          <div className="input-area">
            {currentQ.type === 'multiplechoice' && (
              <>
                <p className="hint">💡 Press the letter key to select, or click an option</p>
                <div className="options-list">
                  {currentQ.options.map((option, index) => {
                    const letter = String.fromCharCode(65 + index);
                    const isSelected = answers[currentQ.id] === option;
                    return (
                      <button
                        key={index}
                        onClick={() => handleMultipleChoiceSelect(option)}
                        className={`option-button ${isSelected ? 'selected' : ''}`}
                      >
                        <div className="option-letter">{letter}</div>
                        <span>{option}</span>
                      </button>
                    );
                  })}
                </div>
              </>
            )}

            {currentQ.type === 'text' && currentQ.id === 'email' && (
              <input
                ref={inputRef}
                type="text"
                value={answers[currentQ.id] || ''}
                onChange={(e) => handleEmailChange(e.target.value)}
                placeholder={currentQ.placeholder}
                className={`text-input ${emailError ? 'input-error' : ''} ${shake ? 'shake' : ''}`}
              />
            )}

            {currentQ.type === 'text' && currentQ.id !== 'email' && (
              <input
                ref={inputRef}
                type="text"
                value={answers[currentQ.id] || ''}
                onChange={(e) => setAnswers({ ...answers, [currentQ.id]: e.target.value })}
                placeholder={currentQ.placeholder}
                className="text-input"
              />
            )}

            {currentQ.type === 'textarea' && (
              <textarea
                ref={inputRef}
                value={answers[currentQ.id] || ''}
                onChange={(e) => setAnswers({ ...answers, [currentQ.id]: e.target.value })}
                placeholder={currentQ.placeholder}
                rows={4}
                className="textarea-input"
              />
            )}
          </div>

          {currentQ.type !== 'multiplechoice' && (
            <button onClick={handleNext} className="ok-button">
              {currentQuestion === questions.length - 1 ? 'Submit' : 'OK'}
              <span className="enter-hint">press <strong>Enter ↵</strong></span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default App;